import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

/**
 * client-api has no analytics/metrics endpoint for ads yet (no spend/click/engagement
 * tables exist). `data` defaults to an all-zero 6-day series so these charts render
 * correctly today and only need a real `data` prop once that endpoint exists.
 */
const EMPTY_SERIES = Array.from({ length: 6 }, (_, i) => ({
  day: `Day ${i + 1}`,
  spend: 0,
  conversions: 0,
  clicks: 0,
  likes: 0,
  follows: 0,
}));

function ChartCard({ title, totals, bg, children }) {
  return (
    <div className={`rounded-2xl p-5 mb-4 ${bg}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-900">{title}</h3>
        {totals?.right && <span className="font-semibold text-sm">{totals.right}</span>}
      </div>
      <ResponsiveContainer width="100%" height={220}>
        {children}
      </ResponsiveContainer>
      {totals?.tiles && (
        <div className="grid grid-cols-2 gap-4 mt-4">
          {totals.tiles.map((tile) => (
            <div key={tile.label} className="bg-white rounded-xl p-3">
              <p className="text-xs text-gray-500">{tile.label}</p>
              <p className="text-xl font-bold">{tile.value}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdReportCharts({ data = EMPTY_SERIES }) {
  const totalSpend = data.reduce((sum, d) => sum + (d.spend || 0), 0);
  const totalConversions = data.reduce((sum, d) => sum + (d.conversions || 0), 0);
  const totalClicks = data.reduce((sum, d) => sum + (d.clicks || 0), 0);
  const totalLikes = data.reduce((sum, d) => sum + (d.likes || 0), 0);
  const totalFollows = data.reduce((sum, d) => sum + (d.follows || 0), 0);

  return (
    <div>
      <h2 className="text-lg font-semibold mb-3">Your Ad Report</h2>

      <ChartCard
        title="Spend & Conversions"
        bg="bg-[#EAF6EE]"
        totals={{
          tiles: [
            { label: "Total Spend", value: `$ ${totalSpend}` },
            { label: "Total Conversions", value: totalConversions },
          ],
        }}
      >
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="day" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="spend" name="Spend" stroke="#0f7a44" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="conversions" name="Conversion" stroke="#9A1B2F" strokeWidth={2} dot={false} />
        </LineChart>
      </ChartCard>

      <ChartCard
        title="Clicks"
        bg="bg-[#FBF6DD]"
        totals={{ right: `Total Clicks: ${totalClicks}` }}
      >
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="day" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="clicks" name="Clicks" stroke="#9C8A1E" strokeWidth={2} dot={false} />
        </LineChart>
      </ChartCard>

      <ChartCard
        title="Engagement (Likes and Follows)"
        bg="bg-[#FBEAEA]"
        totals={{
          tiles: [
            { label: "Total Likes", value: totalLikes },
            { label: "Total Follows", value: totalFollows },
          ],
        }}
      >
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="day" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="likes" name="Likes" stroke="#9A1B2F" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="follows" name="Follows" stroke="#2ecf8a" strokeWidth={2} dot={false} />
        </LineChart>
      </ChartCard>
    </div>
  );
}
